import React, { useEffect, useState } from 'react'
import {
  getAssessment,
  createAssessment,
  updateAssessment,
  requestAssessmentApproval,
  submitAssessmentApprovalDecision,
  getScoreComparison,
  createNote,
  AssessmentInput,
  getActiveRateCalculatorSettings,
  TapRateCalculatorSettings,
} from '@/api/tapResources'
import { getTapAssignees, TapAssignee, updateApplication } from '@/api/tapApplications'
import { TapApplication, TapAssessment, TapCalculatedRateSet } from '@/types/index'
import { Modal } from '@/components/ui/Modal'
// Feature #229: client-side mirror of the rate calculator + per-type input
// label/unit. Per the #229 corrective comment the Assessment tab now shows
// Cost/Buy/Sell as $/hr uniformly across employment types, with $/day as
// a secondary block — so we no longer need a "show annual?" helper.
import {
  calculateRates,
  rateInputMeta,
} from '@/utils/rateCalculator'
// Bug #247: needed so we can hide the Record Decision button from
// everyone except the user the assessment was assigned to.
import { useAuth } from '@/auth/useAuth'

// Feature #156 — Assessment tab. Capture role suitability, salary range and
// preferred employment type, surface the interviewer score comparison, and
// request / record one-up approval.
interface AssessmentTabProps {
  applicationId: string
  // Feature #248: Position Title is mirrored at the top of the
  // Assessment form. The field lives on ``tap_application``, not on
  // ``tap_assessment``, so the tab needs the parent application object
  // (for the current value) and an updated-callback (so edits in this
  // tab make the Summary tab's Review Notes refresh too).
  application?: TapApplication
  onApplicationUpdated?: () => void
}

// Feature #229: canonical 4 employment types — FT/PT use the Permanent
// calculator, Casual and Contractor have their own paths. Legacy values
// like "Permanent Full Time" / "Fixed Term" / "Contract" are still accepted
// by the backend's canonical_employment_type() normaliser so historical
// rows keep computing rates, but new selections are restricted to the
// canonical set.
const EMPLOYMENT_TYPES = ['Full Time', 'Part Time', 'Casual', 'Contractor']
const SUITABILITY_OPTIONS = ['HighlySuitable', 'Suitable', 'Marginal', 'NotSuitable']

// Feature #194: currency formatter — displays values as $123,456.78.
const fmtCurrency = (val?: number | null): string => {
  if (val == null) return '—'
  return `$${val.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// Feature #229 §4.2: dimension-specific formatters. Annual was dropped
// per the #229 corrective comment — kept the formatter type signatures
// consistent across hourly + daily so the same RateBlock component works
// for both blocks. 2dp with unit suffix so reviewers don't have to guess.
const fmtHourly = (val?: number | null): string => {
  if (val == null) return '—'
  return `$${val.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / hr`
}
const fmtDaily = (val?: number | null): string => {
  if (val == null) return '—'
  return `$${val.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / day`
}

// Feature #194: narrower input style for rate fields (6 chars + dollar sign).
const rateInputStyle: React.CSSProperties = {
  width: '130px',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
  textAlign: 'right',
}

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-black)',
  border: '1px solid var(--color-light-grey)',
}

interface ScoreComparisonRow {
  interviewer_user_id?: string
  interviewer_name?: string
  interview_type?: string
  score?: number | null
  recommendation?: string | null
}

// Feature #229: dimension block on the Assessment tab — three rows
// (Cost / Buy / Sell), two columns (Proposed / Max). Same shape used for
// Annual, Hourly, and Day dimensions; the formatter and field-pick
// closures decide which numbers to surface.
interface RateBlockProps {
  title: string
  proposed: TapCalculatedRateSet | null
  max: TapCalculatedRateSet | null
  formatter: (n?: number | null) => string
  pick: (s: TapCalculatedRateSet | null) => { cost?: number; buy?: number; sell?: number }
}

const RateBlock: React.FC<RateBlockProps> = ({ title, proposed, max, formatter, pick }) => {
  const p = pick(proposed)
  const m = pick(max)
  const cellRight: React.CSSProperties = { padding: '4px 16px', textAlign: 'right' }
  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-dark-grey)', textTransform: 'uppercase', marginBottom: '4px' }}>
        {title}
      </div>
      <table style={{ borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ color: 'var(--color-dark-grey)' }}>
            <th style={{ padding: '4px 16px 4px 0', textAlign: 'left', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase' }}></th>
            <th style={{ padding: '4px 16px', textAlign: 'right', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase' }}>Proposed</th>
            <th style={{ padding: '4px 16px', textAlign: 'right', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase' }}>Max</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: '4px 16px 4px 0', fontWeight: 500 }}>Cost</td>
            <td style={cellRight}>{formatter(p.cost)}</td>
            <td style={cellRight}>{formatter(m.cost)}</td>
          </tr>
          <tr>
            <td style={{ padding: '4px 16px 4px 0', fontWeight: 500 }}>Buy</td>
            <td style={cellRight}>{formatter(p.buy)}</td>
            <td style={cellRight}>{formatter(m.buy)}</td>
          </tr>
          <tr>
            <td style={{ padding: '4px 16px 4px 0', fontWeight: 500 }}>Sell</td>
            <td style={cellRight}>{formatter(p.sell)}</td>
            <td style={cellRight}>{formatter(m.sell)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const AssessmentTab: React.FC<AssessmentTabProps> = ({
  applicationId,
  application,
  onApplicationUpdated,
}) => {
  const [assessment, setAssessment] = useState<TapAssessment | null>(null)
  const [loading, setLoading] = useState(true)
  const [comparison, setComparison] = useState<ScoreComparisonRow[] | null>(null)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  // Bug #247: hide Record Decision unless the current user is the
  // assessment's designated approver. Backend enforces the same gate;
  // this just keeps the button from showing to people who'd otherwise
  // get a 403 on click.
  const { user: currentUser } = useAuth()

  // Feature #248: Position Title at the top of the Assessment tab,
  // mirroring the field on Summary → Review Notes. Both write to the
  // same ``tap_application.position_title`` column; a save here fires
  // the ``onApplicationUpdated`` callback so the parent re-fetches the
  // application and the Summary tab's value updates on next view. The
  // local-state shadow lets the user edit without committing on every
  // keystroke; commit on blur (or Enter) keeps the save quiet.
  const [positionTitleDraft, setPositionTitleDraft] = useState(application?.positionTitle || '')
  const [positionTitleSaving, setPositionTitleSaving] = useState(false)
  useEffect(() => {
    setPositionTitleDraft(application?.positionTitle || '')
  }, [application?.positionTitle])

  const commitPositionTitle = async () => {
    if (!application) return
    const trimmed = positionTitleDraft.trim()
    if ((trimmed || '') === (application.positionTitle || '')) return
    setPositionTitleSaving(true)
    try {
      await updateApplication(application.id, {
        positionTitle: trimmed || undefined,
      })
      onApplicationUpdated?.()
    } catch {
      // Revert to the parent's value on failure so the field doesn't
      // visually claim a save happened that didn't.
      setPositionTitleDraft(application.positionTitle || '')
      alert('Failed to update Position Title')
    } finally {
      setPositionTitleSaving(false)
    }
  }

  // Feature #229: active rate calculator settings, loaded once on mount.
  // Used for the in-edit live preview (the saved view uses
  // assessment.calculatedRates which the server already computed).
  const [rateSettings, setRateSettings] = useState<TapRateCalculatorSettings | null>(null)

  const [roleSuitability, setRoleSuitability] = useState('')
  const [salaryMin, setSalaryMin] = useState('')
  const [salaryMax, setSalaryMax] = useState('')
  const [salaryCurrency, setSalaryCurrency] = useState('AUD')
  const [salaryUnit, setSalaryUnit] = useState('Annual')
  const [employmentType, setEmploymentType] = useState('')
  const [lineManagerUserId, setLineManagerUserId] = useState('')
  const [debriefNotes, setDebriefNotes] = useState('')
  // Feature #184: rate columns.
  const [buyRate, setBuyRate] = useState('')
  const [costRate, setCostRate] = useState('')
  const [sellRate, setSellRate] = useState('')
  // Feature #194: proposed/max rate split.
  const [rateProposed, setRateProposed] = useState('')
  const [buyRateProposed, setBuyRateProposed] = useState('')
  const [buyRateMax, setBuyRateMax] = useState('')
  const [costRateProposed, setCostRateProposed] = useState('')
  const [costRateMax, setCostRateMax] = useState('')
  const [sellRateProposed, setSellRateProposed] = useState('')
  const [sellRateMax, setSellRateMax] = useState('')

  // Bug #183: searchable manager picker instead of GUID text input.
  // Bug #230: single combobox-style control — typing into the search box
  // filters a floating dropdown (kept open while focused). The previous
  // implementation rendered a search input AND a select side-by-side
  // ("double control" UX bug).
  const [managers, setManagers] = useState<TapAssignee[]>([])
  const [managerSearch, setManagerSearch] = useState('')
  const [managersLoaded, setManagersLoaded] = useState(false)
  const [managerDropdownOpen, setManagerDropdownOpen] = useState(false)
  const managerDropdownRef = React.useRef<HTMLDivElement>(null)

  const [approvers, setApprovers] = useState<TapAssignee[]>([])
  const [approverModalOpen, setApproverModalOpen] = useState(false)
  const [selectedApproverId, setSelectedApproverId] = useState('')

  const [decisionModalOpen, setDecisionModalOpen] = useState(false)
  const [decisionValue, setDecisionValue] = useState<'Approved' | 'Rejected'>('Approved')
  const [decisionNote, setDecisionNote] = useState('')

  const resetForm = (a: TapAssessment | null) => {
    setRoleSuitability(a?.roleSuitability || '')
    setSalaryMin(a?.determinedSalaryMin?.toString() || '')
    setSalaryMax(a?.determinedSalaryMax?.toString() || '')
    setSalaryCurrency(a?.salaryCurrency || 'AUD')
    setSalaryUnit(a?.salaryUnit || 'Annual')
    setEmploymentType(a?.determinedEmploymentType || '')
    setLineManagerUserId(a?.lineManagerUserId || '')
    // Bug #183: show manager name in search box when editing.
    if (a?.lineManagerUserId && managers.length > 0) {
      const match = managers.find((m) => m.id === a.lineManagerUserId)
      setManagerSearch(match ? (match.displayName || match.email) : '')
    } else {
      setManagerSearch('')
    }
    setDebriefNotes(a?.debriefNotes || '')
    setBuyRate(a?.buyRate?.toString() || '')
    setCostRate(a?.costRate?.toString() || '')
    setSellRate(a?.sellRate?.toString() || '')
    // Feature #194: proposed/max rate split.
    setRateProposed(a?.rateProposed?.toString() || '')
    setBuyRateProposed(a?.buyRateProposed?.toString() || '')
    setBuyRateMax(a?.buyRateMax?.toString() || '')
    setCostRateProposed(a?.costRateProposed?.toString() || '')
    setCostRateMax(a?.costRateMax?.toString() || '')
    setSellRateProposed(a?.sellRateProposed?.toString() || '')
    setSellRateMax(a?.sellRateMax?.toString() || '')
  }

  const load = async () => {
    setLoading(true)
    try {
      const a = await getAssessment(applicationId)
      setAssessment(a)
      resetForm(a)
      try {
        const cmp = (await getScoreComparison(applicationId)) as unknown
        if (Array.isArray(cmp)) {
          setComparison(cmp as ScoreComparisonRow[])
        } else if (cmp && typeof cmp === 'object' && 'items' in (cmp as Record<string, unknown>)) {
          const items = (cmp as { items?: unknown }).items
          setComparison(Array.isArray(items) ? (items as ScoreComparisonRow[]) : [])
        } else {
          setComparison(null)
        }
      } catch {
        setComparison(null)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  // Bug #230: close the manager dropdown on outside click so it behaves
  // like the other comboboxes in the codebase (attendees picker, compose
  // From-address picker).
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (managerDropdownRef.current && !managerDropdownRef.current.contains(e.target as Node)) {
        setManagerDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Feature #229: load the active rate calculator settings once. We don't
  // need to retry on applicationId change — settings are global.
  useEffect(() => {
    getActiveRateCalculatorSettings()
      .then(setRateSettings)
      .catch(() => setRateSettings(null))
  }, [])

  // Live in-edit preview. While editing the user changes rateProposed /
  // salaryMax / employmentType — we recompute on each keystroke so the
  // displayed Cost/Buy/Sell numbers match what the server will return on
  // save.
  const liveProposed: TapCalculatedRateSet | null = editing
    ? calculateRates(rateSettings, employmentType, parseFloat(rateProposed || salaryMin) || null)
    : (assessment?.calculatedRates?.proposed ?? null)
  const liveMax: TapCalculatedRateSet | null = editing
    ? calculateRates(rateSettings, employmentType, parseFloat(salaryMax) || null)
    : (assessment?.calculatedRates?.max ?? null)
  // The employment-type label used to drive the rate-input label/unit and
  // the dimensions shown in the computed display.
  const empTypeForDisplay = editing ? employmentType : (assessment?.determinedEmploymentType || '')

  // Bug #183: load managers for the searchable select.
  // WI #274: anyone can be a line manager, so pass include_all_active=true to
  // skip the /tap/assignees endpoint's TAP-role gate and surface every active
  // LabUser. Previously the picker only showed users holding MANAGE_TAP or
  // PLATFORM_ADMIN, which made it impossible to pick a real line manager
  // outside the HR team.
  const ensureManagers = async () => {
    if (managersLoaded) return
    try {
      const list = await getTapAssignees(undefined, undefined, true)
      setManagers(list)
    } catch {
      // swallow
    }
    setManagersLoaded(true)
  }

  // Bug #195: filter approvers to only users holding the tap_approver role.
  const ensureApprovers = async () => {
    if (approvers.length === 0) {
      try {
        const list = await getTapAssignees(undefined, 'tap_approver')
        setApprovers(list)
      } catch {
        // swallow — picker will show an empty state
      }
    }
  }

  const payload = (): AssessmentInput => ({
    roleSuitability: roleSuitability || undefined,
    determinedSalaryMin: salaryMin ? parseFloat(salaryMin) : undefined,
    determinedSalaryMax: salaryMax ? parseFloat(salaryMax) : undefined,
    salaryCurrency: salaryCurrency || undefined,
    salaryUnit: salaryUnit || undefined,
    determinedEmploymentType: employmentType || undefined,
    lineManagerUserId: lineManagerUserId || undefined,
    debriefNotes: debriefNotes || undefined,
    buyRate: buyRate ? parseFloat(buyRate) : undefined,
    costRate: costRate ? parseFloat(costRate) : undefined,
    sellRate: sellRate ? parseFloat(sellRate) : undefined,
    // Feature #194: proposed/max rate split.
    rateProposed: rateProposed ? parseFloat(rateProposed) : undefined,
    buyRateProposed: buyRateProposed ? parseFloat(buyRateProposed) : undefined,
    buyRateMax: buyRateMax ? parseFloat(buyRateMax) : undefined,
    costRateProposed: costRateProposed ? parseFloat(costRateProposed) : undefined,
    costRateMax: costRateMax ? parseFloat(costRateMax) : undefined,
    sellRateProposed: sellRateProposed ? parseFloat(sellRateProposed) : undefined,
    sellRateMax: sellRateMax ? parseFloat(sellRateMax) : undefined,
  })

  const handleSave = async () => {
    setSaving(true)
    try {
      const updated = assessment
        ? await updateAssessment(applicationId, payload())
        : await createAssessment(applicationId, payload())
      // Feature #197: auto-create a note when debrief notes are saved.
      if (debriefNotes && debriefNotes !== (assessment?.debriefNotes || '')) {
        try {
          await createNote(applicationId, `[Debrief Notes] ${debriefNotes}`, 'Assessment')
        } catch { /* supplementary */ }
      }
      setAssessment(updated)
      setEditing(false)
    } catch {
      alert('Failed to save assessment')
    } finally {
      setSaving(false)
    }
  }

  const handleRequestApproval = async () => {
    if (!selectedApproverId) return
    setSaving(true)
    try {
      await requestAssessmentApproval(applicationId, selectedApproverId)
      setApproverModalOpen(false)
      setSelectedApproverId('')
      await load()
    } catch {
      alert('Failed to request approval')
    } finally {
      setSaving(false)
    }
  }

  const handleDecision = async () => {
    setSaving(true)
    try {
      const updated = await submitAssessmentApprovalDecision(
        applicationId,
        decisionValue,
        decisionNote || undefined,
      )
      setAssessment(updated)
      setDecisionModalOpen(false)
      setDecisionNote('')
    } catch {
      alert('Failed to record decision')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>

  const approvalStatus = assessment?.approvalStatus || 'NotRequested'
  const statusColor = approvalStatus === 'Approved'
    ? '#16a34a'
    : approvalStatus === 'Rejected'
      ? '#dc2626'
      : approvalStatus === 'Pending'
        ? '#d97706'
        : 'var(--color-dark-grey)'

  return (
    <div>
      {/* Feature #248: Position Title mirrored from Summary tab.
          Renders inline at the top of the Assessment tab so the
          assessor can correct any typos before committing rates +
          suitability + manager. Edits sync back to the same
          ``tap_application.position_title`` field SummaryEditor
          writes — see commitPositionTitle handler. */}
      {application && (
        <div style={cardStyle}>
          <label style={labelStyle}>Position Title</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              value={positionTitleDraft}
              onChange={(e) => setPositionTitleDraft(e.target.value)}
              onBlur={commitPositionTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  ;(e.target as HTMLInputElement).blur()
                }
              }}
              placeholder="e.g. Senior Data Analyst"
              style={{ ...inputStyle, maxWidth: '500px' }}
            />
            {positionTitleSaving && (
              <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>Saving…</span>
            )}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--color-dark-grey)', marginTop: '4px' }}>
            Synced with Summary → Review Notes. Saves on blur.
          </div>
        </div>
      )}

      {/* Score comparison — Requirements §3.6.2 */}
      {comparison && comparison.length > 0 && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600 }}>Interviewer scores</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-dark-grey)' }}>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-light-grey)' }}>Interviewer</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-light-grey)' }}>Round</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-light-grey)' }}>Score</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-light-grey)' }}>Recommendation</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((row, idx) => (
                <tr key={idx}>
                  <td style={{ padding: '6px 8px' }}>{row.interviewer_name || row.interviewer_user_id || '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{row.interview_type || '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{row.score ?? '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{row.recommendation || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Determinations */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Determinations</h3>
          {!editing && (
            <button
              onClick={() => {
                resetForm(assessment)
                setEditing(true)
              }}
              style={secondaryBtn}
            >
              {assessment ? 'Edit' : 'Start assessment'}
            </button>
          )}
        </div>

        {!editing ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <div style={labelStyle}>Role Suitability</div>
              <div style={{ fontSize: '14px' }}>{assessment?.roleSuitability || '—'}</div>
            </div>
            <div>
              <div style={labelStyle}>Employment Type</div>
              <div style={{ fontSize: '14px' }}>{assessment?.determinedEmploymentType || '—'}</div>
            </div>
            {/* Feature #229: rate input meta switches by Employment Type. */}
            {(() => {
              const meta = rateInputMeta(empTypeForDisplay)
              return (
                <>
                  <div>
                    <div style={labelStyle}>{meta.label} (Proposed)</div>
                    <div style={{ fontSize: '14px' }}>
                      {fmtCurrency(assessment?.rateProposed ?? assessment?.determinedSalaryMin)}
                      {meta.unit && <span style={{ color: 'var(--color-dark-grey)', fontSize: '12px' }}> {meta.unit}</span>}
                    </div>
                  </div>
                  <div>
                    <div style={labelStyle}>{meta.label} (Max)</div>
                    <div style={{ fontSize: '14px' }}>
                      {fmtCurrency(assessment?.determinedSalaryMax)}
                      {meta.unit && <span style={{ color: 'var(--color-dark-grey)', fontSize: '12px' }}> {meta.unit}</span>}
                    </div>
                  </div>
                </>
              )
            })()}
            <div>
              <div style={labelStyle}>Manager</div>
              <div style={{ fontSize: '14px' }}>
                {assessment?.lineManagerUserId
                  ? (assessment.lineManagerDisplayName || managers.find((m) => m.id === assessment.lineManagerUserId)?.displayName || assessment.lineManagerUserId)
                  : '—'}
              </div>
            </div>
            {/* Feature #229: computed Cost/Buy/Sell display, dimension-aware
                per Employment Type (Annual+Day for FT/PT, Hourly+Day for
                Casual/Contractor). All values come live from the server's
                ``calculated_rates`` block — no client-side computation in
                view mode. */}
            <div style={{ gridColumn: '1 / span 2' }}>
              {!assessment?.calculatedRates && (
                <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', padding: '8px 0' }}>
                  Rate calculator settings not configured — see TAP Settings → Rate Calculator.
                </div>
              )}
              {assessment?.calculatedRates && (
                <>
                  <div style={{ fontSize: '11px', color: 'var(--color-dark-grey)', marginBottom: '4px' }}>
                    {assessment.calculatedRates.settingsFyLabel
                      ? `Computed from ${assessment.calculatedRates.settingsFyLabel} Rate Calculator settings`
                      : 'Computed live'}
                  </div>
                  {/* Per #229 corrective comment: per-hour is primary and
                      uniform across all employment types. Day rate is a
                      secondary block — falls out of the formulas at no
                      extra cost. Annual figures are not displayed. */}
                  <RateBlock
                    title="Hourly"
                    proposed={liveProposed}
                    max={liveMax}
                    formatter={fmtHourly}
                    pick={(s) => ({ cost: s?.costPerHour, buy: s?.buyPerHour, sell: s?.sellPerHour })}
                  />
                  <RateBlock
                    title="Day"
                    proposed={liveProposed}
                    max={liveMax}
                    formatter={fmtDaily}
                    pick={(s) => ({ cost: s?.costPerDay, buy: s?.buyPerDay, sell: s?.sellPerDay })}
                  />
                </>
              )}
            </div>
            {assessment?.debriefNotes && (
              <div style={{ gridColumn: '1 / span 2' }}>
                <div style={labelStyle}>Debrief Notes</div>
                <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{assessment.debriefNotes}</div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {/* Feature #231: Role Suitability + Employment Type on the same
                top row at 50/50. Employment Type drives the rate input
                labels/units rendered immediately below, so picking it
                first matches the natural fill order down the form. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Role Suitability</label>
                <select
                  value={roleSuitability}
                  onChange={(e) => setRoleSuitability(e.target.value)}
                  style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
                >
                  <option value="">—</option>
                  {SUITABILITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Employment Type</label>
                <select
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value)}
                  style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
                >
                  <option value="">—</option>
                  {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            {/* Feature #229: rate input row — label/unit driven by Employment Type. */}
            {(() => {
              const meta = rateInputMeta(employmentType)
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>{meta.label} (Proposed)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        step="0.01"
                        value={rateProposed || salaryMin}
                        onChange={(e) => { setRateProposed(e.target.value); setSalaryMin(e.target.value) }}
                        style={rateInputStyle}
                        placeholder={meta.placeholder}
                      />
                      {meta.unit && <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>{meta.unit}</span>}
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>{meta.label} (Max)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        step="0.01"
                        value={salaryMax}
                        onChange={(e) => setSalaryMax(e.target.value)}
                        style={rateInputStyle}
                        placeholder={meta.placeholder}
                      />
                      {meta.unit && <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>{meta.unit}</span>}
                    </div>
                  </div>
                </div>
              )
            })()}
            {/* Feature #229: live computed Cost/Buy/Sell preview, recalculated
                in-edit on every keystroke via the client-side rateCalculator
                mirror. Same dimension rules as view mode. */}
            <div>
              {!rateSettings && (
                <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                  Rate calculator settings not configured — see TAP Settings → Rate Calculator.
                </div>
              )}
              {rateSettings && (
                <>
                  <div style={{ fontSize: '11px', color: 'var(--color-dark-grey)', marginBottom: '4px' }}>
                    Live preview from {rateSettings.fyLabel} settings — these are the values that will be displayed after save.
                  </div>
                  {/* Per #229 corrective comment: per-hour is primary and
                      uniform; day rate stays as a secondary block. */}
                  <RateBlock
                    title="Hourly"
                    proposed={liveProposed}
                    max={liveMax}
                    formatter={fmtHourly}
                    pick={(s) => ({ cost: s?.costPerHour, buy: s?.buyPerHour, sell: s?.sellPerHour })}
                  />
                  <RateBlock
                    title="Day"
                    proposed={liveProposed}
                    max={liveMax}
                    formatter={fmtDaily}
                    pick={(s) => ({ cost: s?.costPerDay, buy: s?.buyPerDay, sell: s?.sellPerDay })}
                  />
                </>
              )}
            </div>
            {/* Feature #231: Employment Type used to live here in the middle
                of the form. It moved to the top row beside Role Suitability
                so the user picks it before the rate inputs that depend on
                it. */}
            {/* Bug #230: single combobox — search input owns the value
                shown to the user; selecting a row from the floating dropdown
                stamps lineManagerUserId and writes the display string back
                into the input. Clearing the input clears the selection. */}
            <div ref={managerDropdownRef} style={{ position: 'relative' }}>
              <label style={labelStyle}>Manager</label>
              <input
                value={managerSearch}
                onChange={(e) => {
                  setManagerSearch(e.target.value)
                  // Typing invalidates any previously-selected manager
                  // until the user picks a row from the dropdown.
                  if (lineManagerUserId) setLineManagerUserId('')
                  setManagerDropdownOpen(true)
                }}
                onFocus={() => { ensureManagers(); setManagerDropdownOpen(true) }}
                placeholder="Search for a manager…"
                style={inputStyle}
              />
              {managerDropdownOpen && managersLoaded && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  maxHeight: '220px',
                  overflowY: 'auto',
                  backgroundColor: 'var(--color-white)',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  marginTop: '4px',
                  zIndex: 10,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                  {(() => {
                    const q = managerSearch.toLowerCase()
                    const filtered = managers.filter((m) => {
                      if (!q) return true
                      return (
                        (m.displayName || '').toLowerCase().includes(q) ||
                        (m.email || '').toLowerCase().includes(q)
                      )
                    }).slice(0, 20)
                    if (filtered.length === 0) {
                      return (
                        <div style={{ padding: '8px 12px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                          No matching managers
                        </div>
                      )
                    }
                    return filtered.map((m) => (
                      <div
                        key={m.id}
                        onClick={() => {
                          setLineManagerUserId(m.id)
                          setManagerSearch(m.displayName || m.email || '')
                          setManagerDropdownOpen(false)
                        }}
                        style={{
                          padding: '8px 12px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--color-light-grey)',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-light-grey)' }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-white)' }}
                      >
                        <div style={{ fontWeight: 500 }}>{m.displayName || m.email}</div>
                        {m.jobTitle && (
                          <div style={{ fontSize: '11px', color: 'var(--color-dark-grey)' }}>{m.jobTitle}</div>
                        )}
                      </div>
                    ))
                  })()}
                </div>
              )}
            </div>
            <div>
              <label style={labelStyle}>Debrief Notes</label>
              <textarea
                value={debriefNotes}
                onChange={(e) => setDebriefNotes(e.target.value)}
                rows={5}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(false)} style={secondaryBtn} disabled={saving}>Cancel</button>
              <button onClick={handleSave} style={primaryBtn} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Approval */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 600 }}>Approval</h3>
            <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)' }}>
              Status: <strong style={{ color: statusColor }}>{approvalStatus}</strong>
              {assessment?.approverUserId && (
                <> · Approver: <strong>{assessment.approverDisplayName || assessment.approverUserId}</strong></>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={async () => {
                await ensureApprovers()
                setApproverModalOpen(true)
              }}
              style={secondaryBtn}
              disabled={!assessment}
            >
              Request approval
            </button>
            {approvalStatus === 'Pending' &&
              assessment?.approverUserId &&
              currentUser?.id === assessment.approverUserId && (
                <button
                  onClick={() => {
                    setDecisionValue('Approved')
                    setDecisionNote('')
                    setDecisionModalOpen(true)
                  }}
                  style={primaryBtn}
                >
                  Record decision
                </button>
              )}
          </div>
        </div>
      </div>

      {/* Approver picker modal */}
      <Modal
        isOpen={approverModalOpen}
        title="Request approval"
        onClose={() => setApproverModalOpen(false)}
        size="sm"
        isDirty={selectedApproverId !== ''}
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setApproverModalOpen(false)} style={secondaryBtn}>Cancel</button>
            <button onClick={handleRequestApproval} style={primaryBtn} disabled={!selectedApproverId || saving}>
              {saving ? 'Sending…' : 'Request'}
            </button>
          </div>
        }
      >
        <label style={labelStyle}>Approver</label>
        <select
          value={selectedApproverId}
          onChange={(e) => setSelectedApproverId(e.target.value)}
          style={inputStyle}
        >
          <option value="">Select…</option>
          {approvers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName || u.email}{u.jobTitle ? ` (${u.jobTitle})` : ''}
            </option>
          ))}
        </select>
      </Modal>

      {/* Approval decision modal */}
      <Modal
        isOpen={decisionModalOpen}
        title="Approval decision"
        onClose={() => setDecisionModalOpen(false)}
        size="sm"
        isDirty={decisionNote !== ''}
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setDecisionModalOpen(false)} style={secondaryBtn}>Cancel</button>
            <button onClick={handleDecision} style={primaryBtn} disabled={saving}>
              {saving ? 'Saving…' : 'Record'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Decision</label>
            <select
              value={decisionValue}
              onChange={(e) => setDecisionValue(e.target.value as 'Approved' | 'Rejected')}
              style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
            >
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Note</label>
            <textarea
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AssessmentTab
